import api from "./api";
import type {
  TreatmentOverviewItem,
  TreatmentOverviewFilters,
  PatientSearchResult,
} from "@/types/treatment-overview";

const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Build query params string from filter state.
 */
const buildFilterParams = (
  filters?: Partial<TreatmentOverviewFilters>
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters?.startDate) params.append("start_date", filters.startDate);
  if (filters?.endDate) params.append("end_date", filters.endDate);
  if (filters?.providerType && filters.providerType.length > 0)
    filters.providerType.forEach((t) => params.append("provider_type", t));
  if (filters?.adviceType && filters.adviceType.length > 0)
    filters.adviceType.forEach((t) => params.append("advice_type", t));
  return params;
};

/**
 * Consultation flow — fetch treatment overview by appointment ID.
 * GET patient/{appointmentId}/treatment/overview/
 */
export const fetchTreatmentOverview = async (
  appointmentId: string,
  filters?: Partial<TreatmentOverviewFilters>
): Promise<TreatmentOverviewItem[]> => {
  const params = buildFilterParams(filters);
  const queryString = params.toString();
  const url = `/patient/${appointmentId}/treatment/overview/${queryString ? `?${queryString}` : ""}`;
  const response = await api.get<TreatmentOverviewItem[]>(url);
  return response.data;
};

/**
 * Admin flow — fetch treatment overview by patient ID (v2 endpoint).
 * GET patient/v2/treatment/overview/?patient_id=
 */
export const fetchTreatmentOverviewByPatient = async (
  patientId: string,
  filters?: Partial<TreatmentOverviewFilters>
): Promise<TreatmentOverviewItem[]> => {
  const params = buildFilterParams(filters);
  params.append("patient_id", patientId);
  // Preserve the historical lower bound, but cap the default end date at today.
  if (!filters?.startDate) params.set("start_date", "2024-01-01");
  if (!filters?.endDate) params.set("end_date", formatDateForApi(new Date()));
  const url = `/patient/v2/treatment/overview/?${params.toString()}`;
  const response = await api.get<TreatmentOverviewItem[]>(url);
  return response.data;
};

/**
 * Search patients by name or ID (admin flow).
 * GET users/list/?name={query}
 */
export const searchPatients = async (
  query: string
): Promise<PatientSearchResult[]> => {
  const response = await api.get<PatientSearchResult[]>(
    `/users/list/?name=${encodeURIComponent(query)}`
  );
  return response.data;
};
